import { Box, Button, Divider, Grid, Link, Stack, Typography } from '@mui/material';
import { t } from 'i18next';
import AQtiveGuardFullLogo from '../../assets/images/aqtive-guard-full-logo.svg';
import SSOIcon from '../../assets/images/sso-icon.svg';
import { PageLoader } from '../../common/components/PageLoader';
import { BaseController, BaseState } from '../../common/controllers/BaseController';
import { AppConfigContext } from '../../common/services/AppConfigContext';
import { UrlHelper } from '../../common/utils/UrlHelper';
import { bind } from '../../common/utils/decorators';
import { ErrorNotification, SuccessNotification } from '../../common/utils/notifications/Notification';
import { DocumentTitle } from '../../layout/components/DocumentTitle';
import { PATHS } from '../../routing/routes';
import { LoginForm } from '../components/LoginForm';
import { AuthorizationService } from '../services/AuthorizationService';
import { PresetConfig, PresetService } from '../services/PresetService';
import { LoginCredentials } from '../types';
import { isAqueductEnabled, sanitizeNextUrl } from '../utils';

type Props = {};

type State = BaseState & {
  loading: boolean;
};

export class LoginController extends BaseController<Props, State> {
  private readonly authService = new AuthorizationService();
  private readonly presetService = new PresetService();

  private presets: PresetConfig;
  private nextLink: string | null;
  private errorMessage: string | null;

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.state = { ...super.getBaseState(), loading: true };

    // Initial presets, with anonymous feature flags
    this.presets = AppConfigContext.getPresets();

    // Backend app redirects to login with param `?next=` to indicate redirection
    this.nextLink = UrlHelper.getQueryParam('next', null);

    // In a OIDC authentication flow, errors from the provider are passed in the
    // `?error=` param: display them to inform the user of why it came back here
    this.errorMessage = UrlHelper.getQueryParam('error', null);

    // Clear all search parameters
    if (process.env.NODE_ENV === 'production') {
      // In dev mode, `StrictMode` causes the controller to be rendered twice: search
      // params are removed, and the second load behaves like the "accidental access"
      // flow, which tries to redirect to `ORGANIZATION_DASHBOARD` and loops.
      UrlHelper.clearQueryParams();
    }
  }

  componentDidMount() {
    super.componentDidMount();

    if (this.errorMessage) {
      this.showMessage(new ErrorNotification(this.errorMessage));
    }

    this.setState({ loading: false });
  }

  componentWillUnmount(): void {
    super.componentWillUnmount();

    this.authService.cancelAllRequests();
    this.presetService.cancelAllRequests();
  }

  render() {
    const { oidc = null, account = null } = this.presets ?? {};
    const { endpoint: openIdLink = '', buttonLabel: openIdButtonLabel = '' } = oidc ?? {};
    const useAqueduct = isAqueductEnabled() && oidc !== null;

    if (this.state.loading) {
      return (
        <>
          <DocumentTitle loading={true} fragments={[]} />
          <PageLoader />
        </>
      );
    }

    return this.renderBase(
      <>
        <DocumentTitle loading={false} fragments={[t('signIn')]} />
        <Grid container justifyContent="center" alignItems="center" sx={{ height: '100vh', backgroundColor: 'backdrops.light' }}>
          <Grid item xs={10} sm={8} md={6} lg={5}>
            <Stack spacing={2}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <AQtiveGuardFullLogo role="img" title={t('a11y.login.mainLogo')} />
              </Box>

              {!useAqueduct && (
                <>
                  <Typography variant="h4" component="h1" sx={{ color: 'text.primary' }}>
                    {t('signIn')}
                  </Typography>

                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    {t('pleaseSignIntoYourAccount')}
                  </Typography>

                  <LoginForm onSubmit={this.handleFormSubmit} />

                  {account && (
                    <Typography variant="body1">
                      <Link
                        // Use browser navigation for external WebAccount link
                        component="a"
                        href={account.forgotPassword}
                      >
                        {t('forgotPassword')}
                      </Link>
                    </Typography>
                  )}
                </>
              )}

              {oidc !== null && (
                <>
                  {!useAqueduct && (
                    <Divider textAlign="center">
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        {t('or')}
                      </Typography>
                    </Divider>
                  )}

                  <Button
                    type="button"
                    variant="text"
                    href={openIdLink}
                    // Use browser navigation for external OIDC link
                    LinkComponent="a"
                    fullWidth
                    startIcon={<SSOIcon role="img" title={t('a11y.login.ssoIcon')} />}
                  >
                    {openIdButtonLabel}
                  </Button>
                </>
              )}
            </Stack>
          </Grid>
        </Grid>
      </>
    );
  }

  @bind
  private async handleFormSubmit(loginCredentials: LoginCredentials) {
    try {
      const apiKey = await this.authService.login(loginCredentials.username, loginCredentials.password);

      AppConfigContext.setToken(apiKey, this.presets.sessionDuration);

      // The session is now established, update the presets to load feature flags for the
      // current user and override the anonymous context
      await this.refreshPresets();

      this.showMessage(new SuccessNotification(t('loggedIn')));

      // Appease TS: if `this.nextLink` were null, we would have redirected to `PATHS.ORGANIZATION_DASHBOARD`
      const next = sanitizeNextUrl(this.nextLink ?? PATHS.ORGANIZATION_DASHBOARD);
      this.redirect(next);
    } catch (e: unknown) {
      this.handleError(e);
    }
  }

  private async refreshPresets() {
    try {
      const presets = await this.presetService.getPresets();
      AppConfigContext.setPresets(presets);
    } catch (e: unknown) {
      this.handleError(e, undefined, true);
    }
  }
}
